/**
 * The startup benchmark's verdict (spec §4.4). Pure: it sees only what each
 * completed cycle returned and the reference, so every rule is testable
 * without a worker, a canvas or a GPU.
 */
import type { Keypoint } from "@tensorflow-models/pose-detection";
import type { BenchmarkReference } from "./benchmarkReference";
import { boxIoU, type PersonBox } from "./personBox";

export const GATED_JOINTS = ["left_wrist", "right_wrist", "left_ankle", "right_ankle"];
/** A reference joint the verdict may judge confidence against. */
export const REFERENCE_CONFIDENT_SCORE = 0.5;
/** Per-frame median device/reference confidence below this = collapsed. */
export const FAIL_CONFIDENCE_RATIO = 0.4;
/** Per-frame median displacement above this fraction of frame height = wrong. */
export const FAIL_DISPLACEMENT_RATIO = 0.08;
/** Frames that must fail for the verdict to be `fail`. */
export const FAIL_MIN_FRAMES = 2;
/** Fewer completed cycles cannot cover the four frames. */
export const MIN_COMPLETED_CYCLES = 4;
/** `WARMUP_CYCLES_DISCARDED` — excluded from timing, never from accuracy. */
const TIMING_DISCARD = 2;

export type BenchmarkVerdict = "pass" | "fail" | "unknown";

export interface BenchmarkCycle {
  frameIndex: number;
  /** The first pose's keypoints, or null when the detect resolved with no pose. */
  keypoints: Keypoint[] | null;
  /** The height model's person box, when this cycle ran the object pass. */
  personBox: PersonBox | null;
  objectPassRan: boolean;
  cycleMs: number;
}

export interface BenchmarkResult {
  verdict: BenchmarkVerdict;
  unknownReason: string | null;
  framesUsed: number;
  cyclesCompleted: number;
  gatedDevMaxRatio: number | null;
  gatedDevMedianRatio: number | null;
  confidenceRatioMin: number | null;
  confidenceRatioMedian: number | null;
  repeatJitterMaxRatio: number | null;
  personFoundRatio: number | null;
  personBoxIoUMin: number | null;
}

/**
 * Cycle i's frame. Rotates so the object-detection cycles (every 4th — the real
 * loop's stride, i = 0, 4, 8) land on frames 0, 1 and 2, not always on frame 0.
 */
export function benchmarkFrameIndex(i: number): number {
  return (i + Math.floor(i / 4)) % 4;
}

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const s = values.slice().sort(function (a, b) {
    return a - b;
  });
  const mid = s.length >> 1;
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

function round3(v: number | null): number | null {
  return v === null ? null : Math.round(v * 1000) / 1000;
}

function byName<K extends { name?: string }>(kps: K[] | null, name: string): K | null {
  if (!kps) return null;
  for (let i = 0; i < kps.length; i++) if (kps[i].name === name) return kps[i];
  return null;
}

function unknown(cyclesCompleted: number, reason: string): BenchmarkResult {
  return {
    verdict: "unknown",
    unknownReason: reason,
    framesUsed: 0,
    cyclesCompleted,
    gatedDevMaxRatio: null,
    gatedDevMedianRatio: null,
    confidenceRatioMin: null,
    confidenceRatioMedian: null,
    repeatJitterMaxRatio: null,
    personFoundRatio: null,
    personBoxIoUMin: null,
  };
}

export function scoreBenchmark(input: {
  reference: BenchmarkReference | null;
  unknownReason: string | null;
  cycles: BenchmarkCycle[];
}): BenchmarkResult {
  const cycles = input.cycles;
  try {
    if (input.unknownReason) return unknown(cycles.length, input.unknownReason);
    if (!input.reference) return unknown(cycles.length, "assets-unavailable");
    if (cycles.length < MIN_COMPLETED_CYCLES) return unknown(cycles.length, "too-few-cycles");
    const reference = input.reference;

    const allDisp: number[] = [];
    const allRatio: number[] = [];
    let jitterMax: number | null = null;
    let iouMin: number | null = null;
    let failedFrames = 0;
    let framesUsed = 0;
    let personEligible = 0;
    let personFound = 0;

    for (let f = 0; f < reference.frames.length; f++) {
      const refFrame = reference.frames[f];
      const frameCycles = cycles.filter(function (c) {
        return c.frameIndex === f;
      });
      if (frameCycles.length === 0) continue;
      framesUsed++;

      const cycleConf: number[] = [];
      const cycleDisp: number[] = [];
      for (let c = 0; c < frameCycles.length; c++) {
        const cycle = frameCycles[c];
        const ratios: number[] = [];
        const disps: number[] = [];
        for (let j = 0; j < GATED_JOINTS.length; j++) {
          const ref = byName(refFrame.keypoints, GATED_JOINTS[j]);
          if (!ref) continue;
          const dev = byName(cycle.keypoints, GATED_JOINTS[j]);
          if (ref.score >= REFERENCE_CONFIDENT_SCORE) {
            // No pose, or no score, is confidence 0 — the collapse itself.
            const devScore = dev && typeof dev.score === "number" ? dev.score : 0;
            ratios.push(devScore / ref.score);
          }
          if (dev) {
            disps.push(Math.sqrt(Math.pow(dev.x - ref.x, 2) + Math.pow(dev.y - ref.y, 2)) / refFrame.height);
          }
        }
        for (let k = 0; k < ratios.length; k++) allRatio.push(ratios[k]);
        for (let k = 0; k < disps.length; k++) allDisp.push(disps[k]);
        const mc = median(ratios);
        if (mc !== null) cycleConf.push(mc);
        const md = median(disps);
        if (md !== null) cycleDisp.push(md);

        if (cycle.objectPassRan && refFrame.personBox) {
          personEligible++;
          if (cycle.personBox) {
            personFound++;
            const iou = boxIoU(cycle.personBox, refFrame.personBox);
            iouMin = iouMin === null ? iou : Math.min(iouMin, iou);
          }
        }
      }

      // Repeats of a frame combine by median, so one bad repeat of three does
      // not fail it. A frame with no pose at all is judged on confidence alone.
      const frameConf = median(cycleConf);
      const frameDisp = median(cycleDisp);
      if (
        (frameConf !== null && frameConf < FAIL_CONFIDENCE_RATIO) ||
        (frameDisp !== null && frameDisp > FAIL_DISPLACEMENT_RATIO)
      ) {
        failedFrames++;
      }

      // Fresh detects of the same frame should agree exactly on a healthy phone.
      const withPose = frameCycles.filter(function (c) {
        return c.keypoints !== null;
      });
      for (let a = 0; a < withPose.length; a++) {
        for (let b = a + 1; b < withPose.length; b++) {
          for (let j = 0; j < GATED_JOINTS.length; j++) {
            const ka = byName(withPose[a].keypoints, GATED_JOINTS[j]);
            const kb = byName(withPose[b].keypoints, GATED_JOINTS[j]);
            if (!ka || !kb) continue;
            const jitter = Math.sqrt(Math.pow(ka.x - kb.x, 2) + Math.pow(ka.y - kb.y, 2)) / refFrame.height;
            jitterMax = jitterMax === null ? jitter : Math.max(jitterMax, jitter);
          }
        }
      }
    }

    return {
      verdict: failedFrames >= FAIL_MIN_FRAMES ? "fail" : "pass",
      unknownReason: null,
      framesUsed,
      cyclesCompleted: cycles.length,
      gatedDevMaxRatio: round3(allDisp.length ? Math.max.apply(null, allDisp) : null),
      gatedDevMedianRatio: round3(median(allDisp)),
      confidenceRatioMin: round3(allRatio.length ? Math.min.apply(null, allRatio) : null),
      confidenceRatioMedian: round3(median(allRatio)),
      repeatJitterMaxRatio: round3(jitterMax),
      personFoundRatio: personEligible ? round3(personFound / personEligible) : null,
      personBoxIoUMin: round3(iouMin),
    };
  } catch (e) {
    return unknown(cycles.length, "score-error");
  }
}

/** Median and worst cycle time without the warm-up cycles — for calibration rows. */
export function cycleTimes(cycles: BenchmarkCycle[]): {
  cycleMedianMs: number | null;
  cycleMaxMs: number | null;
} {
  const ms: number[] = [];
  for (let i = TIMING_DISCARD; i < cycles.length; i++) ms.push(cycles[i].cycleMs);
  const med = median(ms);
  return {
    cycleMedianMs: med === null ? null : Math.round(med),
    cycleMaxMs: ms.length ? Math.round(Math.max.apply(null, ms)) : null,
  };
}
